import JSZip from 'jszip';
import { logError, logInfo } from '@utils/logger';

/**
 * Export route to specified format
 * @param {Object} routeData - Route data
 * @param {string} format - Export format (kml, kmz, gpx)
 * @returns {Promise<Blob>} - Exported file as blob
 */
export const exportRouteToFormat = async (routeData, format) => {
  try {
    switch (format.toLowerCase()) {
      case 'kml':
        return exportToKML(routeData);
      case 'kmz':
        return exportToKMZ(routeData);
      case 'gpx':
        return exportToGPX(routeData);
      default:
        throw new Error(`Unsupported export format: ${format}`);
    }
  } catch (error) {
    logError(`Failed to export route as ${format}:`, error);
    throw error;
  }
};

/**
 * Export route to KML format
 * @param {Object} routeData - Route data
 * @returns {Promise<Blob>} - KML file as blob
 */
const exportToKML = async (routeData) => {
  try {
    const { points, structures, origin, destination, vehicleHeight } = routeData;
    
    // Create KML content
    let kml = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>OS/OW Route: ${origin} to ${destination}</name>
    <description>Route for vehicle with height ${vehicleHeight.toFixed(2)}m</description>
    
    <!-- Styles -->
    <Style id="routeStyle">
      <LineStyle>
        <color>ff3b82f6</color>
        <width>5</width>
      </LineStyle>
    </Style>
    <Style id="safeStructureStyle">
      <IconStyle>
        <color>ff10b981</color>
        <scale>1.0</scale>
        <Icon>
          <href>http://maps.google.com/mapfiles/kml/shapes/placemark_circle.png</href>
        </Icon>
      </IconStyle>
    </Style>
    <Style id="cautionStructureStyle">
      <IconStyle>
        <color>ff0ea5e9</color>
        <scale>1.0</scale>
        <Icon>
          <href>http://maps.google.com/mapfiles/kml/shapes/placemark_circle.png</href>
        </Icon>
      </IconStyle>
    </Style>
    <Style id="dangerStructureStyle">
      <IconStyle>
        <color>ff3b82f6</color>
        <scale>1.0</scale>
        <Icon>
          <href>http://maps.google.com/mapfiles/kml/shapes/placemark_circle.png</href>
        </Icon>
      </IconStyle>
    </Style>
    
    <!-- Route -->
    <Placemark>
      <name>Route</name>
      <styleUrl>#routeStyle</styleUrl>
      <LineString>
        <tessellate>1</tessellate>
        <coordinates>
          ${points.map(p => `${p.longitude},${p.latitude},0`).join('\n          ')}
        </coordinates>
      </LineString>
    </Placemark>
    
    <!-- Structures -->`;
    
    // Add structures
    structures.forEach(structure => {
      if (!structure.coordinates?.latitude || !structure.coordinates?.longitude) return;
      
      // Determine style based on clearance
      let styleUrl = '#safeStructureStyle';
      const clearance = structure.properties?.clearance;
      
      if (clearance && clearance < vehicleHeight) {
        styleUrl = '#dangerStructureStyle';
      } else if (clearance && clearance < vehicleHeight + 0.5) {
        styleUrl = '#cautionStructureStyle';
      }
      
      kml += `
    <Placemark>
      <name>${structure.name || `Structure ${structure.id}`}</name>
      <description>
        <![CDATA[
          <div>
            <p><strong>Clearance:</strong> ${clearance ? `${clearance.toFixed(2)}m` : 'Unknown'}</p>
            <p><strong>Municipality:</strong> ${structure.properties?.municipality || ''}</p>
            <p><strong>Type:</strong> ${structure.type || ''}</p>
          </div>
        ]]>
      </description>
      <styleUrl>${styleUrl}</styleUrl>
      <Point>
        <coordinates>${structure.coordinates.longitude},${structure.coordinates.latitude},0</coordinates>
      </Point>
    </Placemark>`;
    });
    
    kml += `
  </Document>
</kml>`;
    
    return new Blob([kml], { type: 'application/vnd.google-earth.kml+xml' });
  } catch (error) {
    logError('Failed to export to KML:', error);
    throw error;
  }
};

/**
 * Export route to KMZ format
 * @param {Object} routeData - Route data
 * @returns {Promise<Blob>} - KMZ file as blob
 */
const exportToKMZ = async (routeData) => {
  try {
    const kmlBlob = await exportToKML(routeData);
    const kmlText = await kmlBlob.text();
    
    // Create ZIP file
    const zip = new JSZip();
    zip.file('doc.kml', kmlText);
    
    // Add icons
    const iconPromises = [
      fetch('/icon-192.png').then(res => res.blob()).then(blob => {
        zip.file('icons/icon.png', blob);
      }).catch(() => {
        // Ignore if icon not available
      })
    ];
    
    await Promise.all(iconPromises);
    
    // Generate KMZ file
    return await zip.generateAsync({ type: 'blob' });
  } catch (error) {
    logError('Failed to export to KMZ:', error);
    throw error;
  }
};

/**
 * Export route to GPX format
 * @param {Object} routeData - Route data
 * @returns {Promise<Blob>} - GPX file as blob
 */
const exportToGPX = async (routeData) => {
  try {
    const { points, structures, origin, destination, vehicleHeight } = routeData;
    
    // Create GPX content
    let gpx = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="OverSizeHub" xmlns="http://www.topografix.com/GPX/1/1">
  <metadata>
    <name>OS/OW Route: ${origin} to ${destination}</name>
    <desc>Route for vehicle with height ${vehicleHeight.toFixed(2)}m</desc>
    <time>${new Date().toISOString()}</time>
  </metadata>
  
  <!-- Route -->
  <trk>
    <name>Route</name>
    <trkseg>
      ${points.map(p => `<trkpt lat="${p.latitude}" lon="${p.longitude}"></trkpt>`).join('\n      ')}
    </trkseg>
  </trk>
  
  <!-- Structures -->`;
    
    // Add structures as waypoints
    structures.forEach(structure => {
      if (!structure.coordinates?.latitude || !structure.coordinates?.longitude) return;
      
      const clearance = structure.properties?.clearance;
      
      gpx += `
  <wpt lat="${structure.coordinates.latitude}" lon="${structure.coordinates.longitude}">
    <name>${structure.name || `Structure ${structure.id}`}</name>
    <desc>Clearance: ${clearance ? `${clearance.toFixed(2)}m` : 'Unknown'}</desc>
    <type>Structure</type>
  </wpt>`;
    });
    
    gpx += `
</gpx>`;
    
    return new Blob([gpx], { type: 'application/gpx+xml' });
  } catch (error) {
    logError('Failed to export to GPX:', error);
    throw error;
  }
};

export default exportRouteToFormat;